import React, { useEffect, useId } from 'react';
import { Dialog, Box } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { EMOJI_ICONS, EMOJIS_LIST } from 'features/room-reactions/constants';
import { useRoomReactionsControl } from 'features/toolbar/toolbar/controls/room-reactions/useRoomReactionsControl';
import { Reaction } from 'features/room-reactions/types';
import { closeModal } from 'features/modal/modalSlice';
import { selectToolbarPlacement } from 'features/toolbar/toolbarSlice';
import { useSidePanel } from 'features/side-panels/hooks/useSidePanel';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import NoStylesButton from 'components/NoStylesButton';

const EmojiButton = styled(NoStylesButton)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  border: 'none',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:active': {
    backgroundColor: theme.palette.action.selected,
  },
}));

const EmojiIcon = styled('img')({
  width: '32px',
  height: '32px',
  display: 'block',
});

const RoomReactionsModal = () => {
  const modalId = useId();

  const { sidePanel } = useSidePanel();

  const dispatch = useAppDispatch();
  const toolbarPlacement = useAppSelector(selectToolbarPlacement);

  const { sendReaction, isFeatureAvailable } = useRoomReactionsControl();

  const shouldClose = !isFeatureAvailable;

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal('roomReactions'));
    }
  }, [shouldClose, dispatch]);

  const handleClose = () => {
    dispatch(closeModal('roomReactions'));
  };

  const handleReactionClick = (reaction: Reaction) => {
    sendReaction(reaction);
  };

  return (
    <Dialog
      open={!shouldClose}
      hideBackdrop={!sidePanel}
      maxWidth="xs"
      onClose={handleClose}
      aria-labelledby={`${modalId}-title`}
      sx={
        !sidePanel
          ? {
              '& .MuiDialog-container': {
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              },
            }
          : undefined
      }
      slotProps={{
        paper: {
          sx: !sidePanel
            ? (theme: Theme) => ({
                width: 320,
                marginBottom:
                  toolbarPlacement === 'bottom'
                    ? `calc(var(--toolbar-width) + ${theme.spacing(1)})`
                    : theme.spacing(3),
              })
            : { width: 320 },
        },
      }}
    >
      <Box sx={{ display: 'flex' }}>
        {EMOJIS_LIST.map((reaction) => (
          <EmojiButton
            key={reaction}
            onClick={() => handleReactionClick(reaction)}
            aria-label={reaction}
          >
            <EmojiIcon src={EMOJI_ICONS[reaction]} alt={reaction} />
          </EmojiButton>
        ))}
      </Box>
    </Dialog>
  );
};

export default RoomReactionsModal;
